import { useState, useEffect, useRef } from "react";
import { useNotification } from "./useNotification";
import { useApiErrorHandler } from "./useApiErrorHandler";
import useTokenRefresh from "./useTokenRefresh";
import httpclient from "../Utils";

export const useBayLocation = (options = {}) => {
  const {
    autoFetch = true,
    pagination = { page: 0, pageSize: 20 },
    sorting = [],
    transformData = null,
  } = options;

  const [bayLocations, setBayLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rowCount, setRowCount] = useState(0);

  const prevParams = useRef({
    page: pagination.page,
    pageSize: pagination.pageSize,
    sorting: JSON.stringify(sorting),
  });

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();
  const { getTokenRefreshed: refresh } = useTokenRefresh();

  const fetchBayLocations = async (customParams = {}) => {
    const currentParams = {
      page: pagination.page,
      pageSize: pagination.pageSize,
      sorting: JSON.stringify(sorting),
    };

    try {
      setLoading(true);
      setError(null);

      const params = {
        page: pagination.page + 1,
        limit: pagination.pageSize,
        ...customParams,
      };

      if (sorting.length > 0) {
        const sortField = sorting[0].field;
        const sortOrder = sorting[0].sort;
        params.sortBy = sortField;
        params.sortOrder = sortOrder;
      }

      const res = await httpclient.get("/bin-bay-location", { params });
      const { data } = res;

      if (data.status === 200) {
        const stations = transformData ? transformData(data.data) : data.data;
        setBayLocations(stations);
        setRowCount(data.meta?.total || data.count || 0);

        // Update previous params after successful fetch
        prevParams.current = currentParams;
      } else {
        const errorMsg = data.msg || "Failed to fetch bay locations";
        setError(errorMsg);
        showNotification("error", errorMsg);
      }
    } catch (error) {
      const errorMsg = "Failed to fetch bay locations";
      setError(errorMsg);

      if (error.response?.status === 401) {
        refresh();
      } else {
        handleApiError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoFetch) {
      fetchBayLocations();
    }
  }, [
    autoFetch,
    pagination.page,
    pagination.pageSize,
    JSON.stringify(sorting),
  ]);

  const refreshBayLocations = (params = {}) => {
    fetchBayLocations(params);
  };

  return {
    bayLocations,
    setBayLocations,
    loading,
    setLoading,
    error,
    rowCount,
    fetchBayLocations,
    refresh,
    refreshBayLocations,
  };
};
